import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Toggle from "./Toggle";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  createService,
  fetchAvailableServices,
  updateService,
} from "../services/serviceService";

const EMPTY = { serviceId: "", priceOverride: "", isAvailableOnline: true, isActive: true };

/** An existing offering -> the shape the form works with. */
const toForm = (offering) =>
  offering
    ? {
        serviceId: offering.service_id ?? "",
        // Blank means "charge the catalogue price".
        priceOverride: offering.price_override ?? "",
        isAvailableOnline: Boolean(offering.is_available_online),
        isActive: Boolean(offering.is_active),
      }
    : EMPTY;

/**
 * What this branch offers, and on what terms. The service itself — its timing,
 * booking rules and list price — is edited in the catalogue; a branch only
 * chooses whether to offer it and, when it charges something else, how much.
 *
 * `offering` null means adding: the picker then lists the catalogue services
 * this branch has not taken up yet.
 */
export default function ServiceFormModal({ branchId, service, priceLabel, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [available, setAvailable] = useState([]);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(service);

  useEffect(() => {
    if (!open) return;

    setValues(toForm(service));
    setErrors({});
    setBanner("");
  }, [open, service]);

  // Only when adding: what is left in the catalogue to pick from.
  useEffect(() => {
    if (!open || editing) return;

    fetchAvailableServices(branchId)
      .then(({ services }) => setAvailable(services))
      .catch(() => setAvailable([]));
  }, [open, editing, branchId]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, saving]);

  const chosen = available.find((entry) => entry.id === values.serviceId);

  const submit = async (e) => {
    e.preventDefault();

    if (!editing && !values.serviceId) {
      setErrors({ service_id: [t("services.form.errors.service")] });
      return;
    }

    setSaving(true);
    setErrors({});
    setBanner("");

    try {
      const saved = editing
        ? await updateService(branchId, service.service_id, values)
        : await createService(branchId, values);

      onSaved(saved, editing);
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("services.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const fieldError = (field) => errors[field]?.[0];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-lg font-semibold">
              {editing
                ? t("services.form.editTitle", { name: service.service?.name ?? "" })
                : t("services.form.addTitle")}
            </h2>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            {editing ? (
              <div className="rounded-lg bg-raised px-3 py-2 text-sm">
                <p className="font-medium">{service.service?.name}</p>
                <p className="text-xs text-ink-2">
                  {t("services.minutes", { count: service.total_minutes ?? 0 })}
                  {service.service?.category ? ` · ${service.service.category}` : ""}
                </p>
              </div>
            ) : (
              <div>
                <label className="field-label" htmlFor="service-picker">
                  {t("services.form.service")} <span className="text-danger-text">*</span>
                </label>
                <select
                  id="service-picker"
                  className="field-input"
                  value={values.serviceId}
                  onChange={(e) => setValues({ ...values, serviceId: e.target.value })}
                >
                  <option value="">{t("services.form.selectService")}</option>
                  {available.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                      {entry.category ? ` — ${entry.category.name}` : ""}
                    </option>
                  ))}
                </select>
                {available.length === 0 && (
                  <p className="mt-1 text-xs text-ink-2">{t("services.form.noneLeft")}</p>
                )}
                {fieldError("service_id") && <p className="field-error">{fieldError("service_id")}</p>}
              </div>
            )}

            <div>
              <label className="field-label" htmlFor="service-price">
                {priceLabel ?? t("services.form.price")}
              </label>
              <input
                id="service-price"
                type="number"
                step="0.01"
                min="0"
                dir="ltr"
                className="field-input"
                placeholder={
                  chosen?.price ?? service?.service?.price ?? t("services.form.cataloguePrice")
                }
                value={values.priceOverride}
                onChange={(e) => setValues({ ...values, priceOverride: e.target.value })}
              />
              {/* Blank is the common case: most branches charge the list price. */}
              <p className="mt-1 text-xs text-ink-2">{t("services.form.priceHint")}</p>
              {fieldError("price_override") && (
                <p className="field-error">{fieldError("price_override")}</p>
              )}
            </div>

            <Toggle
              label={t("services.form.online")}
              hint={t("services.form.onlineHint")}
              checked={values.isAvailableOnline}
              onChange={(checked) => setValues({ ...values, isAvailableOnline: checked })}
            />

            <Toggle
              label={t("services.form.active")}
              hint={t("services.form.activeHint")}
              checked={values.isActive}
              onChange={(checked) => setValues({ ...values, isActive: checked })}
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
